import { reduce } from 'lodash/fp';
import { DateTimeLocalizationInfo } from '@mews-framework/globalization';
import { ISOFormat, LocalizationFormatName, LocalizationMethod } from '../enums';
import { formatLuxon, FormatStringName, isValidLuxonFormat, unformatLuxon } from '../utils';
import { Factory, ToLuxon } from './types';

export type FormatTuple = readonly [LocalizationMethod, FormatStringName];

type WithTo<T, M> = {
    [Property in M as `to${Capitalize<string & Property>}`]: (
        entity: T,
        localizationInfo: DateTimeLocalizationInfo | ISOFormat.Date
    ) => string;
};

type WithFrom<T, M> = {
    [Property in M as `from${Capitalize<string & Property>}`]: (
        entity: string,
        localizationInfo: DateTimeLocalizationInfo | ISOFormat.Date,
        timezone?: string
    ) => T;
};

type WithIsValid<M> = {
    [Property in M as `isValid${Capitalize<string & Property>}`]: (
        entity: string,
        localizationInfo: DateTimeLocalizationInfo | ISOFormat.Date
    ) => boolean;
};

type Localized<T, M> = WithTo<T, M> & WithFrom<T, M> & WithIsValid<M>;

export default function localizable<T, F extends FormatTuple>(
    factory: Factory<T>,
    toLuxon: ToLuxon<T>,
    formats: readonly F[] = []
) {
    const toString =
        (formatName: FormatStringName) => (entity: T, localizationInfo: DateTimeLocalizationInfo | ISOFormat.Date) =>
            formatLuxon(LocalizationFormatName[formatName], localizationInfo, toLuxon(entity));

    const fromString =
        (formatName: FormatStringName) =>
        (entityString: string, localizationInfo: DateTimeLocalizationInfo | ISOFormat.Date, timezone?: string) =>
            factory(unformatLuxon(LocalizationFormatName[formatName], localizationInfo, entityString, timezone));

    const isValidString =
        (formatName: FormatStringName) =>
        (entityString: string, localizationInfo: DateTimeLocalizationInfo | ISOFormat.Date) =>
            isValidLuxonFormat(LocalizationFormatName[formatName], localizationInfo, entityString);

    const localizationMethods = reduce<F, Localized<T, F[0]>>(
        (operations, [methodName, formatName]) => ({
            ...operations,
            [`to${methodName}`]: toString(formatName),
            [`from${methodName}`]: fromString(formatName),
            [`isValid${methodName}`]: isValidString(formatName),
        }),
        {} as Localized<T, F[0]>,
        formats
    );

    return { ...localizationMethods };
}
